import { Box, Typography, Link } from '@mui/material'
import { Link as RouterLink, useNavigate } from 'react-router'
import { themeColors } from '../../app/layout/theme'

// ─── Types ────────────────────────────────────────────────────────────────────

export interface BreadcrumbItem {
  /** Text shown for this crumb */
  label: string
  /** If provided, this crumb becomes a clickable link */
  to?: string
}

interface BreadcrumbNavProps {
  /**
   * Array of parent crumbs. The current page title is appended automatically
   * as the last, non-clickable crumb.
   *
   * @example
   * // Renders:  Configuration / User Management
   * items={[{ label: 'Configuration', to: '/settings?tab=admin' }]}
   * current="User Management"
   */
  items: BreadcrumbItem[]
  /** Label for the active/current page (shown bold, non-linked) */
  current: string
  /**
   * When true, renders a ← back arrow before the crumbs that navigates
   * to the last item's `to` path (or calls `onBack` if provided).
   */
  showBack?: boolean
  /** Override what happens when the back arrow is clicked */
  onBack?: () => void
  /** Font size of the crumbs (default: 12.5px) */
  fontSize?: string | number
}

// ─── Component ────────────────────────────────────────────────────────────────

export const BreadcrumbNav = ({
  items,
  current,
  showBack = false,
  onBack,
  fontSize = '12.5px',
}: BreadcrumbNavProps) => {
  const navigate = useNavigate()

  const handleBack = () => {
    if (onBack) {
      onBack()
    } else {
      const lastLink = [...items].reverse().find(i => i.to)
      if (lastLink?.to) navigate(lastLink.to)
      else navigate(-1)
    }
  }

  const allCrumbs: BreadcrumbItem[] = [...items, { label: current }]

  // Parse font size to scale the arrow icon proportionally
  const isLarge = typeof fontSize === 'string' && parseFloat(fontSize) >= 15

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
      {/* Optional back arrow */}
      {showBack && (
        <Box
          onClick={handleBack}
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            color: '#6B7280',
            mr: '4px',
            '&:hover': { color: themeColors.primary },
            transition: 'color 0.15s',
          }}
        >
          <svg width={isLarge ? 20 : 15} height={isLarge ? 20 : 15} viewBox="0 0 24 24" fill="none">
            <path
              d="M19 12H5M5 12L12 19M5 12L12 5"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </Box>
      )}

      {/* Crumbs */}
      {allCrumbs.map((crumb, i) => {
        const isLast = i === allCrumbs.length - 1
        return (
          <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {i > 0 && (
              <Typography
                component="span"
                sx={{
                  fontSize: fontSize,
                  color: '#94a3b8',
                  lineHeight: 1.2,
                  userSelect: 'none',
                  mx: '2px'
                }}
              >
                /
              </Typography>
            )}

            {crumb.to && !isLast ? (
              <Link
                component={RouterLink}
                to={crumb.to}
                underline="none"
                sx={{
                  fontSize: fontSize,
                  fontWeight: 400,
                  color: '#6B7280',
                  lineHeight: 1.2,
                  '&:hover': { color: themeColors.primary, textDecoration: 'underline' },
                  transition: 'color 0.15s',
                }}
              >
                {crumb.label}
              </Link>
            ) : (
              <Typography
                component="span"
                sx={{
                  fontSize: fontSize,
                  fontWeight: isLast ? 700 : 400,
                  color: isLast ? themeColors.text.primary : '#6B7280',
                  lineHeight: 1.2,
                }}
              >
                {crumb.label}
              </Typography>
            )}
          </Box>
        )
      })}
    </Box>
  )
}
